import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Brain, Upload, FileText, X, Loader2, ShoppingCart, Printer,
  ChevronDown, ChevronUp, Package, MapPin, Tag, Info, Download,
  Pencil, Trash2, Plus, Save, XCircle, Sparkles, AlertTriangle, Target
} from 'lucide-react';
import InvoiceScanner from './InvoiceScanner';
import MaterialPlanDialog from './components/MaterialPlanDialog';
import ToolsPlanSection from './components/ToolsPlanSection';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BlueprintViewer3D } from './material/BlueprintViewer3D';
import { BlueprintPanel } from './material/BlueprintPanel';
import { MaterialTable } from './material/MaterialTable';
import { MaterialDetailSheet } from './material/MaterialDetailSheet';
import { ProjectOverviewCard } from './material/ProjectOverviewCard';
import { AIQualityCard, SourcesCard } from './material/InsightCards';
import { AIProgressCard, MaterialEmptyState } from './material/MaterialStates';
import { ExportMenu } from './material/ExportMenu';
import { ViewNav, CostView, SummaryView, PartsView, RoomsView, RefsView } from './material/MaterialViews';
import { PurchaseListDialog } from './material/PurchaseListDialog';
import { useModel3D } from './material/useModel3D';
import { phaseToLayer, modelStats } from './material/model3d';

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const MaterialPlanTab = ({ projectId, project }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null); // plan id being edited
  const [editMaterials, setEditMaterials] = useState([]); // editable copy
  const [saving, setSaving] = useState(false);
  const [aiProfile, setAiProfile] = useState(null);  // Vad AI har lärt sig om kunden
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const viewerApi = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const [view, setView] = useState('material');
  const [highlight, setHighlight] = useState(null);
  const [activePhase, setActivePhase] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showPurchase, setShowPurchase] = useState(false);

  const [form, setForm] = useState({
    job_type: '',
    notes: '',
    project_type: '',
    total_area_m2: '',
    floors: '',
  });
  const [files, setFiles] = useState([]);

  // Cleanup polling on unmount to prevent freeze/memory leak
  useEffect(() => {
    return () => {

      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  useEffect(() => {
    fetchPlans();
    // Hämta AI-lärande-profil för informationsbanner
    api.getMaterialAIProfile().then(res => setAiProfile(res.data)).catch(() => {});
  }, [projectId]);

  // Listen for autorun event from dashboard quick-calc buttons
  useEffect(() => {
    const handler = () => setShowDialog(true);
    window.addEventListener('operio-autorun-material-plan', handler);
    return () => window.removeEventListener('operio-autorun-material-plan', handler);
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await api.getMaterialPlans(projectId);
      setPlans(res.data?.material_plans || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (plan) => {
    setEditingPlan(plan.id);
    setEditMaterials(JSON.parse(JSON.stringify(plan.materials || [])));
  };

  const cancelEditing = () => {
    setEditingPlan(null);
    setEditMaterials([]);
  };

  const updateMaterialField = (idx, field, value) => {
    setEditMaterials(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      // Recalculate total_price when quantity or unit_price changes
      if (field === 'quantity' || field === 'estimated_unit_price') {
        const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(updated[idx].quantity) || 0;
        const price = field === 'estimated_unit_price' ? parseFloat(value) || 0 : parseFloat(updated[idx].estimated_unit_price) || 0;
        updated[idx].estimated_total_price = Math.round(qty * price * 100) / 100;
      }
      return updated;
    });
  };

  const addMaterialRow = () => {
    setEditMaterials(prev => [...prev, {
      name: '', quantity: 1, unit: 'st',
      specific_product: '', brand: '', specification: '',
      estimated_unit_price: 0, estimated_total_price: 0,
      where_to_buy: '', comment: '',
    }]);
  };

  const removeMaterialRow = (idx) => {
    setEditMaterials(prev => prev.filter((_, i) => i !== idx));
  };

  const saveEdits = async (planId) => {
    setSaving(true);
    try {
      await api.updateMaterialPlan(planId, { materials: editMaterials });
      toast.success('Materialplan uppdaterad!');
      setEditingPlan(null);
      setEditMaterials([]);
      fetchPlans();
    } catch (err) {
      toast.error('Kunde inte spara ändringar');
    } finally {
      setSaving(false);
    }
  };

  const deletePlan = async (planId) => {
    if (!window.confirm('Vill du ta bort denna materialplan?')) return;
    try {
      await api.deleteMaterialPlan(planId);
      toast.success('Materialplan borttagen');
      fetchPlans();
    } catch (err) {
      toast.error('Kunde inte ta bort materialplan');
    }
  };

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selected]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleGenerate = async () => {
    if (!form.job_type.trim()) {
      toast.error('Ange typ av jobb');
      return;
    }
    if (!form.project_type) {
      toast.error('Välj projekttyp — behövs för korrekt kalkyl mot branschsnitt');
      return;
    }
    if (!form.total_area_m2 || parseFloat(form.total_area_m2) <= 0) {
      toast.error('Ange total yta (m² BTA) så AI:n kan träffa rätt storleksordning');
      return;
    }
    setGenerating(true);
    try {
      let res;
      if (files.length > 0) {
        const formData = new FormData();
        formData.append('project_id', projectId);
        formData.append('job_type', form.job_type);
        formData.append('estimated_hours', '0');   // AI räknar fram i Arbete-steget
        formData.append('workers', '1');
        formData.append('machines', '0');
        if (form.notes) formData.append('notes', form.notes);
        formData.append('project_type', form.project_type);
        formData.append('total_area_m2', form.total_area_m2);
        if (form.floors) formData.append('floors', form.floors);
        files.forEach(f => formData.append('files', f));
        res = await api.generateMaterialPlanWithFiles(formData);
      } else {
        res = await api.generateMaterialPlan({
          project_id: projectId,
          job_type: form.job_type,
          notes: form.notes || null,
          project_type: form.project_type || null,
          total_area_m2: parseFloat(form.total_area_m2) || null,
          floors: parseInt(form.floors) || null,
        });
      }
      const planId = res.data?.id;
      // Stäng dialog FÖRST, sen toast — undviker Radix race där body:s pointer-events fastnar
      setShowDialog(false);
      setForm({ job_type: '', notes: '', project_type: '', total_area_m2: '', floors: '' });
      setFiles([]);
      // Låt Radix animera ut dialogen innan toasten öppnas
      setTimeout(() => toast.info('AI genererar materialplan... Detta kan ta 30-60 sekunder.'), 250);
      // Poll for completion
      if (planId) {
        setExpandedPlan(planId);
        let pollErrors = 0;
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
          try {
            const planRes = await api.getMaterialPlan(planId);
            const plan = planRes.data;
            pollErrors = 0;
            if (plan.status === 'completed') {
              clearInterval(pollRef.current);
              pollRef.current = null;
              setGenerating(false);
              toast.success('Materialplan genererad!');
              fetchPlans();
            } else if (plan.status === 'failed') {
              clearInterval(pollRef.current);
              pollRef.current = null;
              setGenerating(false);
              toast.error(plan.error_message || 'Materialberäkning misslyckades.');
              fetchPlans();
            }
          } catch (pollErr) {
            pollErrors++;
            // If we get auth errors (401/403), stop immediately — don't keep hammering
            if (pollErr?.response?.status === 401 || pollErr?.response?.status === 403) {
              clearInterval(pollRef.current);
              pollRef.current = null;
              setGenerating(false);
              fetchPlans();
              return;
            }
            if (pollErrors >= 4) {
              clearInterval(pollRef.current);
              pollRef.current = null;
              setGenerating(false);
              toast.error('Kunde inte hämta status. Ladda om sidan.');
              fetchPlans();
            }
          }
        }, 4000);
        // Safety timeout: stop after 3 minuter (Gemini-analys av stora bilder + GPT-generering kan ta tid)
        setTimeout(() => {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setGenerating(false);
            toast.info('Beräkningen tar längre tid än väntat. Den fortsätter i bakgrunden — uppdatera sidan om en stund.');
            fetchPlans();
          }
        }, 180000);
      } else {
        setGenerating(false);
        fetchPlans();
      }
    } catch (err) {
      // Stäng dialog även vid fel så body:s pointer-events lås alltid rensas
      setShowDialog(false);
      toast.error('Materialberäkning misslyckades. Försök igen.');
      console.error(err);
      setGenerating(false);
    }
  };

  const handlePrint = (plan) => {
    const printWindow = window.open('', '_blank');
    const materials = plan.materials || [];
    const rows = materials.map(m =>
      `<tr>
        <td style="padding:6px 10px;border-bottom:1px solid #eee">${m.specific_product || m.name}${m.brand ? ` (${m.brand})` : ''}</td>
        <td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:right">${m.quantity} ${m.unit}</td>
        <td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:right">${m.estimated_unit_price ? m.estimated_unit_price + ' kr' : '—'}</td>
        <td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:right;font-weight:600">${m.estimated_total_price ? m.estimated_total_price + ' kr' : '—'}</td>
        <td style="padding:6px 10px;border-bottom:1px solid #eee">${m.where_to_buy || '—'}</td>
      </tr>`
    ).join('');
    const html = `
      <html><head><title>Inköpslista — ${plan.job_type}</title>
      <style>body{font-family:Arial,sans-serif;max-width:900px;margin:20px auto;color:#1a1a1a}h1{font-size:22px}table{width:100%;border-collapse:collapse}th{text-align:left;padding:8px 10px;background:#f5f5f5;border-bottom:2px solid #ccc;font-size:12px;text-transform:uppercase}td{font-size:13px}tfoot td{font-weight:bold;border-top:2px solid #333;padding:8px 10px}.summary{margin-top:20px;padding:15px;background:#fafafa;border-radius:4px;font-size:13px;line-height:1.6}</style>
      </head><body>
      <h1>Materialplan & Inköpslista</h1>
      <p style="color:#666;margin-bottom:20px">Jobb: ${plan.job_type} | Genererad: ${new Date(plan.created_at).toLocaleDateString('sv-SE')}</p>
      <table>
        <thead><tr><th>Produkt</th><th style="text-align:right">Mängd</th><th style="text-align:right">Pris/st</th><th style="text-align:right">Totalt</th><th>Var att köpa</th></tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr><td colspan="3">TOTAL</td><td style="text-align:right">${plan.total_estimated_cost ? plan.total_estimated_cost.toLocaleString('sv-SE') + ' kr' : '—'}</td><td></td></tr></tfoot>
      </table>
      ${plan.ai_summary ? `<div class="summary"><strong>Sammanfattning:</strong> ${plan.ai_summary}</div>` : ''}
      </body></html>
    `;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    printWindow.location.href = url;
    setTimeout(() => { printWindow.print(); URL.revokeObjectURL(url); }, 500);
  };

  useEffect(() => {
    const p = plans.find((x) => x.id === expandedPlan);
    if (p?.status === 'completed') setSelectedId(p.id);
  }, [plans, expandedPlan]);

  const completed = plans.filter((p) => p.status === 'completed');
  const current = completed.find((p) => p.id === selectedId) || completed[0] || null;
  const generatingPlan = plans.find((p) => p.status === 'generating');
  const failedPlans = plans.filter((p) => p.status === 'failed');
  const model3d = useModel3D(current?.id);
  const stats = modelStats(model3d.model);
  const area = current?.total_area_m2 || stats?.bta || 0;
  const editing = current && editingPlan === current.id;
  const purchaseKey = (m) => `${m.phase}|${m.specific_product || m.name}`;
  const purchaseList = current?.purchase_list || [];

  const togglePurchase = async (m) => {
    const key = purchaseKey(m);
    const next = purchaseList.some((x) => x.key === key)
      ? purchaseList.filter((x) => x.key !== key)
      : [...purchaseList, { key, name: m.specific_product || m.name, phase: m.phase, quantity: m.quantity, unit: m.unit, unit_price: m.estimated_unit_price, total: m.estimated_total_price, supplier: m.where_to_buy }];
    setPlans((ps) => ps.map((p) => (p.id === current.id ? { ...p, purchase_list: next } : p)));
    try {
      await api.updateMaterialPlan(current.id, { purchase_list: next });
      toast.success(next.length > purchaseList.length ? 'Tillagd i inköpslistan' : 'Borttagen från inköpslistan');
    } catch (e) { toast.error('Kunde inte spara inköpslistan'); fetchPlans(); }
  };

  const onPhaseClick = (name) => { setActivePhase(name); setHighlight(phaseToLayer(name)); };
  const startFlow = (mode) => {
    setShowDialog(true);
    if (mode === 'files') setTimeout(() => fileInputRef.current?.click(), 400);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-[#F26B1F] border-t-transparent op-round rounded-full animate-spin" /></div>
  );

  const viewBody = current && ({
    material: <MaterialTable materials={editing ? editMaterials : current.materials || []} area={area} editing={editing} onField={updateMaterialField}
      onAddRow={addMaterialRow} onRemove={removeMaterialRow} onRowClick={setDetail} onPhaseClick={onPhaseClick} activePhase={activePhase} />,
    costs: <CostView materials={current.materials || []} />,
    summary: <SummaryView plan={current} />,
    parts: <PartsView materials={current.materials || []} onPhaseClick={(n) => { onPhaseClick(n); setView('material'); }} />,
    rooms: <RoomsView model={model3d.model} />,
    refs: <RefsView materials={current.materials || []} />,
  })[view];

  return (
    <div className="space-y-6" data-testid="material-plan-tab">
      {(generatingPlan || generating) && <AIProgressCard startedAt={generatingPlan?.created_at} />}
      {!current && !generatingPlan && !generating && <MaterialEmptyState onStart={startFlow} />}

      {current && (
        <>
          <div className="flex flex-col md:flex-row md:items-center gap-3" data-testid="material-toolbar">
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold">Materialberäkning</h2>
              <p className="text-xs text-stone-500">{current.job_type} · {new Date(current.created_at).toLocaleString('sv-SE', { dateStyle: 'medium', timeStyle: 'short' })}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {completed.length > 1 && (
                <Select value={current.id} onValueChange={setSelectedId}>
                  <SelectTrigger className="h-[42px] w-[210px] rounded-xl bg-white" data-testid="material-plan-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{completed.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{new Date(p.created_at).toLocaleDateString('sv-SE')} · {Math.round((p.total_estimated_cost || 0) / 1000).toLocaleString('sv-SE')} tkr</SelectItem>
                  ))}</SelectContent>
                </Select>
              )}
              <button onClick={() => setShowPurchase(true)} className="op-btn-ghost" data-testid="purchase-list-btn"><ShoppingCart size={16} />Inköpslista{purchaseList.length > 0 && <span className="ml-0.5 min-w-[20px] h-5 px-1.5 rounded-full bg-[#F26B1F] text-white text-[11px] font-bold inline-flex items-center justify-center">{purchaseList.length}</span>}</button>
              <ExportMenu plan={current} projectName={project?.name} viewerApi={viewerApi} onPrint={() => handlePrint(current)} />
              {editing ? (
                <>
                  <button onClick={cancelEditing} className="op-btn-ghost" data-testid="cancel-edit-btn"><XCircle size={16} />Avbryt</button>
                  <button onClick={() => saveEdits(current.id)} disabled={saving} className="op-btn-primary" data-testid="save-materials-btn"><Save size={16} />{saving ? 'Sparar…' : 'Spara'}</button>
                </>
              ) : (
                <>
                  <button onClick={() => { setView('material'); startEditing(current); }} className="op-btn-ghost" data-testid={`edit-plan-${current.id}`}><Pencil size={15} />Redigera</button>
                  <button onClick={() => deletePlan(current.id)} className="op-btn-ghost !px-3 text-red-600" data-testid={`delete-plan-${current.id}`} title="Ta bort"><Trash2 size={15} /></button>
                  <button onClick={() => setShowDialog(true)} disabled={generating} className="op-btn-primary" data-testid="generate-material-btn"><Sparkles size={16} />Ny beräkning</button>
                </>
              )}
            </div>
          </div>

          <div className="grid xl:grid-cols-[minmax(0,1fr)_340px] gap-6 items-start">
            <div className="space-y-6 min-w-0">
              <div className="grid lg:grid-cols-[minmax(0,1fr)_260px] gap-4 op-rise">
                <BlueprintViewer3D model3d={model3d} highlight={highlight} onClearHighlight={() => { setHighlight(null); setActivePhase(null); }} apiRef={viewerApi} />
                <BlueprintPanel planId={current.id} model={model3d.model} />
              </div>
              <div className="op-card p-4 sm:p-5 op-rise op-rise-1" data-testid="material-calc-card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-[17px] font-bold">Materialberäkning</h3>
                  <p className="text-xs text-stone-500 op-num">{(current.materials || []).length} rader · {Math.round(current.total_estimated_cost || 0).toLocaleString('sv-SE')} kr</p>
                </div>
                <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
                  <ViewNav view={view} setView={setView} />
                  <div className="flex-1 min-w-0">{viewBody}</div>
                </div>
                {current.benchmark_check?.status && current.benchmark_check.status !== 'no_benchmark' && (
                  <div className={`mt-4 rounded-xl px-4 py-3 text-xs flex items-start gap-2 ${current.benchmark_check.status === 'ok' ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`} data-testid={`benchmark-detail-${current.id}`}>
                    <Target size={14} className="mt-0.5 flex-shrink-0" />{current.benchmark_check.message || 'Jämförd mot svenskt branschsnitt.'}
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-4 op-rise op-rise-2">
              <ProjectOverviewCard project={project || { id: projectId }} plan={current} stats={stats} />
              <AIQualityCard plan={current} model={model3d.model} />
              <SourcesCard plan={current} />
            </div>
          </div>
        </>
      )}

      {failedPlans.length > 0 && (
        <div className="op-card p-4 space-y-2" data-testid="failed-plans">
          <p className="text-sm font-semibold flex items-center gap-2"><AlertTriangle size={15} className="text-amber-500" />Misslyckade beräkningar</p>
          {failedPlans.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3 text-xs rounded-xl bg-red-50 px-3 py-2" data-testid={`plan-${p.id}`}>
              <span className="text-red-700 min-w-0 truncate">{new Date(p.created_at).toLocaleString('sv-SE')} — {p.error_message || 'Okänt fel'}</span>
              <button onClick={() => deletePlan(p.id)} className="text-red-700 font-semibold flex-shrink-0" data-testid={`delete-plan-${p.id}`}>Ta bort</button>
            </div>
          ))}
        </div>
      )}

      <InvoiceScanner
        projectId={projectId}
        onItemsScanned={(items) => {
          if (editingPlan) {
            setEditMaterials((prev) => [...prev, ...items]);
            toast.success(`${items.length} artiklar tillagda i redigeringsläge`);
          } else {
            toast.info('Klicka "Redigera" på materialplanen för att lägga till artiklarna');
          }
        }}
      />

      <MaterialPlanDialog open={showDialog} onOpenChange={setShowDialog} form={form} setForm={setForm} files={files}
        fileInputRef={fileInputRef} onFileChange={handleFileChange} onRemoveFile={removeFile} onGenerate={handleGenerate} generating={generating} />
      <MaterialDetailSheet material={detail} onClose={() => setDetail(null)} inPurchaseList={!!detail && purchaseList.some((x) => x.key === purchaseKey(detail))} onTogglePurchase={togglePurchase} />
      <PurchaseListDialog open={showPurchase} onOpenChange={setShowPurchase} items={purchaseList} projectName={project?.name}
        onRemove={(key) => togglePurchase(current.materials.find((m) => purchaseKey(m) === key) || { phase: key.split('|')[0], name: key.split('|')[1] })} />

      <ToolsPlanSection projectId={projectId} materialPlanId={current?.id} />
    </div>
  );
};

export default MaterialPlanTab;
